import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Autocomplete,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Add } from "@mui/icons-material";
import debounce from "lodash/debounce";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import PATCloseButton from "../components/Buttons/PATCloseButton";
import PATSaveButton from "../components/Buttons/PATSaveButton";
import { getService } from "../reducers/service";
import { setCircularLoading } from "../reducers/UiReducer";
import { serviceNames } from "../utils/constants";
import { createRecordFor } from "../utils/enums";
import { notify } from "../reducers/NotifierReducer";
import PersonDialog from "./PersonDialog";
import { CodeTagTypeIconRender } from "../utils/functions";

const useStyles = makeStyles((theme) => ({
  activeWarrant: {
    background: theme.palette.error.main,
    color: theme.card.bg,
    width: "calc(100% + 32px)",
    padding: "8px 16px",
    margin: "-8px -16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 14,
    "& em": {
      fontStyle: "normal",
      fontSize: 12,
    },
  },
  option: {
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    width: "100%",
    "& em": {
      fontStyle: "normal",
      fontSize: 12,
    },
  },
  warrants: {
    position: "absolute",
    right: 90,
    top: 5,
    opacity: 0.2,
    transition: "all 0.1s",
  },
}));

const SearchPersonDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Select Person",
  confirmMessage,
  filterExistingSSNs = true,
  createFor = createRecordFor.Employee,
  genderOptions,
  raceOptions,
  ethnicityOptions,
  contactInfoOptions,
  suffixOptions,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [existingSSNs, setExistingSSNs] = useState([]);
  const [openPersonDialog, setOpenPersonDialog] = useState(false);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  useEffect(() => {
    const fetchExistingSSNs = async () => {
      try {
        const service =
          createFor === createRecordFor.Employee
            ? getService(serviceNames.personnel)
            : getService(serviceNames.perApplicant);
        const response = await service.find({
          query: { $limit: -1, isDeleted: false },
        });
        const data = Array.isArray(response) ? response : response.data || [];
        setExistingSSNs(data);
      } catch (error) {
        dispatch(notify("Failed to fetch existing SSNs", "error"));
      }
    };

    if (open && filterExistingSSNs) {
      fetchExistingSSNs();
    }
  }, [open, filterExistingSSNs]);

  const searchPeople = async (
    search = "",
    viewDeleted = false,
    offset = 0,
    limit = 100
  ) => {
    const service = getService(serviceNames.searchPeople);
    const result = await service.find({
      query: { search, viewDeleted, offset, limit },
    });
    return result;
  };

  const fetchPersons = useMemo(
    () =>
      debounce(async (query) => {
        try {
          dispatch(setCircularLoading(true));
          const res = await searchPeople(query);
          const data = Array.isArray(res) ? res : res.data || [];

          const existingSSNList = existingSSNs
            .map((p) => p.ssn?.trim())
            .filter(Boolean);

          const filtered = filterExistingSSNs
            ? data.filter(
                (person) =>
                  !person.SSN || !existingSSNList.includes(person.SSN.trim())
              )
            : data;
          setOptions(filtered);
        } catch (err) {
          dispatch(notify("Failed to fetch persons", "error"));
          setOptions([]);
        } finally {
          dispatch(setCircularLoading(false));
        }
      }, 500),
    [existingSSNs, filterExistingSSNs]
  );

  useEffect(() => {
    if (inputValue.length >= 3) {
      fetchPersons(inputValue);
    } else {
      setOptions([]);
    }
  }, [inputValue, fetchPersons]);

  const handlePersonSelect = (event, newValue) => {
    if (newValue) {
      setSelectedPerson(newValue);
      setConfirmDialogOpen(true);
    }
  };

  const resetDialog = () => {
    setInputValue("");
    setOptions([]);
    setSelectedPerson(null);
    setConfirmDialogOpen(false);
  };

  const onDialogClose = () => {
    resetDialog();
    onClose();
  };

  const handleAddClick = () => {
    setOpenPersonDialog(true);
  };

  const onSaveRecord = () => {
    onConfirm(selectedPerson);
    setConfirmDialogOpen(false);
    resetDialog();
  };
  const renderPersonName = (person) => {
    if (!person) return "";
    return `${person.FirstName || ""} ${person.MiddleName || ""} ${
      person.LastName || ""
    }`.trim();
  };

  const renderOptions = (option) => {
    let parts = [];

    if (option.LastName) parts.push(`${option.LastName},`);
    if (option.FirstName) parts.push(option.FirstName);
    if (option.MiddleName) parts.push(option.MiddleName);
    if (option.Race) {
      const raceItem = dictionary.codeRace.find((r) => r.Code === option.Race);
      const race = raceItem ? raceItem.Description : "";
      parts.push(`| Race: ${race}`);
    }
    if (option.Sex) {
      const sexItem = dictionary.codeSex.find((s) => s.Code === option.Sex);
      const sex = sexItem ? sexItem.Description : "";
      parts.push(`| Sex: ${sex}`);
    }
    if (option.BirthDate)
      parts.push(`| DOB: ${moment(option.BirthDate).format("MM/DD/YYYY")}`);
    if (option.OLN) parts.push(`| OLN: ${option.OLN}`);
    if (option.SSN) parts.push(`| SSN: ${option.SSN}`);
    if (option.PersonID) parts.push(`| ${option.PersonID}`);

    let tagIcons = null;
    if (option.Tags) {
      const json_data = JSON.parse(option.Tags);
      tagIcons = (
        <span style={{ display: "flex", gap: "6px", marginLeft: "6px" }}>
          {json_data.map((tag, idx) => {
            const icon = tag.c[0].Icon;
            const tagData = {
              created: tag.Created,
              createdBy: tag.CreatedBy,
              updated: tag.Updated,
              updatedBy: tag.UpdatedBy,
              type: tag.Type,
              typeDescription: tag.c?.[0]?.TypeDescription,
              description: tag.Value,
            };
            const TagIcon = CodeTagTypeIconRender(
              icon,
              "small",
              "20px",
              tagData
            );

            return <span key={idx}>{TagIcon}</span>;
          })}
        </span>
      );

      parts.push(" | ");
    }

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
        }}
        className={
          option.ActiveWarrant ? classes.activeWarrant : classes.option
        }
      >
        <span>{parts.join(" ")}</span>
        {tagIcons}
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          onDialogClose();
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={options}
            value={selectedPerson}
            isOptionEqualToValue={(option, value) =>
              option.ptsPersonID === value.ptsPersonID
            }
            getOptionLabel={(option) => renderPersonName(option)}
            onInputChange={(e, val) => setInputValue(val)}
            onChange={handlePersonSelect}
            renderOption={(props, option, { selected }) => (
              <Box
                component="li"
                {...props}
                sx={{
                  borderBottom: "1px solid #ddd",
                  backgroundColor: selected ? "#f44336" : "white",
                  color: selected ? "white" : "black",
                }}
              >
                {renderOptions(option)}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search"
                placeholder="Enter at least 3 letters..."
                fullWidth
                sx={{ mt: 2 }}
                InputProps={{
                  ...params.InputProps,
                  autoComplete: "off",
                  startAdornment: (
                    <IconButton onClick={handleAddClick}>
                      <Add fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <PATCloseButton
            onClick={() => {
              onDialogClose();
            }}
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedPerson
              ? confirmMessage
                ? confirmMessage(selectedPerson)
                : `Are you sure you want to select "${renderPersonName(
                    selectedPerson
                  )}"?`
              : "No person selected."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <PATSaveButton onClick={() => onSaveRecord()} />
          <PATCloseButton onClick={() => setConfirmDialogOpen(false)} />
        </DialogActions>
      </Dialog>
      {openPersonDialog && (
        <PersonDialog
          open={openPersonDialog}
          onClose={() => setOpenPersonDialog(false)}
          genderOptions={genderOptions}
          raceOptions={raceOptions}
          ethnicityOptions={ethnicityOptions}
          contactInfoOptions={contactInfoOptions}
          suffixOptions={suffixOptions}
          createRecordFor={createFor}
        />
      )}
    </>
  );
};

export default SearchPersonDialog;
